import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

const USER_TOKEN_KEY = 'mmtess_admin_user_token'; // AM_DB → x-am-user-authorization
const API_TOKEN_KEY = 'mmtess_admin_api_token'; // AM    → x-am-authorization
const USER_KEY = 'mmtess_admin_user';

/** Authenticated admin profile (subset of the `users` DTO; password never present). */
export interface AuthUser {
  _id?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  userType?: string; // 'ADMIN' | 'SUB_ADMIN' | 'CUSTOMER' — admin panel only admits admin roles
  [k: string]: unknown;
}

/** Admin Login response: mints BOTH tokens in one call + a role-scoped AM token (tagsen-style). */
interface LoginData {
  token: string; // AM_DB (database-user) token → x-am-user-authorization
  refreshToken?: string; // AM_DB refresh token
  amToken: string; // AM (role-scoped API-user) token → x-am-authorization (used by native schema CRUD)
  user: AuthUser;
}

/**
 * Admin auth — single `Admin Login` Custom API that mints both tokens (§4 [C 15:36];
 * the separate Get Token endpoint was removed). No `default` password in the SPA.
 *   POST {customApiBase}/admin-login {email,password}
 *     → { token (AM_DB), amToken (AM), user }
 * `amToken` is sent as `x-am-authorization` on native schema-CRUD calls (apiHeadersInterceptor);
 * `token` as `x-am-user-authorization`. Both persist so a reload keeps the session; on a 401/403
 * the interceptor clears them and routes to /login.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);

  private _userToken = signal<string>(localStorage.getItem(USER_TOKEN_KEY) ?? '');
  private _apiToken = signal<string>(localStorage.getItem(API_TOKEN_KEY) ?? environment.devAuthToken);
  private _user = signal<AuthUser | null>(readJson(USER_KEY));

  readonly userToken = this._userToken.asReadonly(); // x-am-user-authorization
  readonly apiToken = this._apiToken.asReadonly(); // x-am-authorization
  readonly user = this._user.asReadonly();
  readonly isAuthenticated = computed(() => !!this._apiToken());
  /** Backend already gates login to admin roles; this is for FE display/route hints only. */
  readonly isAdmin = computed(() => {
    const t = (this._user()?.userType || '').toString();
    return t === 'ADMIN' || t === 'SUB_ADMIN';
  });
  /**
   * Full ADMIN only. Rotating third-party credentials is not a delegated-operator action, so
   * SUB_ADMIN is excluded — matching CREDENTIAL_ADMIN_ROLES in the backend's ProviderConfig, which
   * is the real enforcement point. This just avoids routing SUB_ADMINs to a page that 403s.
   */
  readonly isFullAdmin = computed(() => (this._user()?.userType || '').toString() === 'ADMIN');

  /** Log in; store both tokens + profile. Throws on failure (caller shows the message). */
  async login(email: string, password: string): Promise<void> {
    const res = await firstValueFrom(
      this.http.post<unknown>(`${environment.customApiBase}/admin-login`, { email, password }),
    );
    const data = unwrap<LoginData>(res);
    if (!data?.amToken) throw new Error('Login succeeded but no API token was returned.');
    this.setUserToken(data.token ?? '');
    this.setApiToken(data.amToken);
    this.setUser(data.user ?? null);
  }

  setUserToken(token: string): void {
    this._userToken.set(token);
    persist(USER_TOKEN_KEY, token);
  }

  setApiToken(token: string): void {
    this._apiToken.set(token);
    persist(API_TOKEN_KEY, token);
  }

  setUser(user: AuthUser | null): void {
    this._user.set(user);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  }

  logout(): void {
    this._userToken.set('');
    this._apiToken.set('');
    this._user.set(null);
    localStorage.removeItem(USER_TOKEN_KEY);
    localStorage.removeItem(API_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  }
}

/** Unwrap the standard API Maker envelope `{ success, data, errors }`. */
function unwrap<T>(res: unknown): T {
  if (res && typeof res === 'object') {
    const r = res as { success?: boolean; data?: unknown; errors?: { message?: string }[] };
    if (r.success === false) {
      throw new Error(r.errors?.map((e) => e.message).filter(Boolean).join('; ') || 'Login failed.');
    }
    if (r.data !== undefined) return r.data as T;
  }
  return res as T;
}

function persist(key: string, value: string): void {
  if (value) localStorage.setItem(key, value);
  else localStorage.removeItem(key);
}

function readJson(key: string): AuthUser | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}
